import { Placement } from '@ng-bootstrap/ng-bootstrap';

/**
 * Hover-title helper for score tables.
 * Replaces the native HTML title tooltip with the app-styled hover
 * (white text on a translucent black background).
 */

/** ng-bootstrap tooltip class used by header breadcrumbs (styled hover). */
export const SCORE_HOVER_TITLE_TOOLTIP_CLASS = 'custom-tooltip-background';

/**
 * Prefer bottom-left so the tooltip stays next to the hovered value
 * instead of centering under a wide table cell. Fallbacks are used when
 * that side has no room in the table/card.
 */
export const SCORE_HOVER_TITLE_PLACEMENT: Placement[] = ['bottom-left', 'top-left', 'bottom-right', 'top-right'];

const LAYOUT_PADDING = 8;

function getReferenceElement(reference: Element | { contextElement?: Element }): Element | null {
  if (reference instanceof Element) {
    return reference;
  }
  return reference.contextElement ?? null;
}

function getLayoutBoundary(reference: Element): DOMRect {
  const layout =
    reference.closest('.table-container') ||
    reference.closest('.card') ||
    document.documentElement;
  const box = layout.getBoundingClientRect();
  return {
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    top: Math.max(box.top, 0),
    left: Math.max(box.left, 0),
    right: Math.min(box.right, window.innerWidth),
    bottom: Math.min(box.bottom, window.innerHeight),
    toJSON: box.toJSON,
  };
}

/**
 * Popper's default flip uses the viewport. The trends chart sits below the
 * table in that viewport, so last-row tooltips never flip and overflow the card.
 * This modifier flips against the Score card / table instead.
 */
const flipWithinLayout = {
  name: 'flipWithinLayout',
  enabled: true,
  phase: 'main' as const,
  fn({ state }: { state: { placement: string; reset?: boolean; rects: { popper: { width: number; height: number } }; elements: { reference: Element | { contextElement?: Element } } } }) {
    const reference = getReferenceElement(state.elements.reference);
    if (!reference) {
      return;
    }

    const boundary = getLayoutBoundary(reference);
    const refRect = reference.getBoundingClientRect();
    const popperHeight = state.rects.popper.height;
    const popperWidth = state.rects.popper.width;
    const [side, align] = state.placement.split('-');

    const spaceBelow = boundary.bottom - refRect.bottom;
    const spaceAbove = refRect.top - boundary.top;
    const spaceRight = boundary.right - refRect.left;
    const spaceLeft = refRect.right - boundary.left;

    let nextSide = side;
    let nextAlign = align;

    if (side === 'bottom' && spaceBelow < popperHeight + LAYOUT_PADDING && spaceAbove > spaceBelow) {
      nextSide = 'top';
    } else if (side === 'top' && spaceAbove < popperHeight + LAYOUT_PADDING && spaceBelow > spaceAbove) {
      nextSide = 'bottom';
    }

    if (align === 'start' && spaceRight < popperWidth && spaceLeft >= popperWidth) {
      nextAlign = 'end';
    } else if (align === 'end' && spaceLeft < popperWidth && spaceRight >= popperWidth) {
      nextAlign = 'start';
    }

    const nextPlacement = nextAlign ? `${nextSide}-${nextAlign}` : nextSide;
    if (nextPlacement !== state.placement) {
      state.placement = nextPlacement;
      state.reset = true;
    }
  },
};

/**
 * Use fixed positioning so the tooltip is anchored to the viewport,
 * not a table-cell offset parent (which places it far from the hover target).
 */
export function applyScoreHoverTitlePopperOptions<T extends {
  strategy?: string;
  modifiers?: Array<{ name?: string; enabled?: boolean }>;
}>(options: T): T {
  const modifiers = (options.modifiers ?? []).map((modifier) =>
    modifier?.name === 'flip' ? { ...modifier, enabled: false } : modifier
  );

  return {
    ...options,
    strategy: 'fixed',
    modifiers: [...modifiers, flipWithinLayout],
  };
}

/**
 * Returns the hover-title text for the styled tooltip, or null when none should show.
 */
export function getScoreHoverTitle(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'object') {
    return null;
  }
  const text = String(value).trim();
  return text.length ? text : null;
}
